import React, { useState, useEffect } from 'react';
import { setDoc, onSnapshot } from 'firebase/firestore';
import { moodsDoc } from '../db';
import { UserProfile, Screen } from '../App';
import { recordActivity } from '../utils/activityFeed';
import { queueNotification } from '../hooks/usePushNotification';

interface Props { user: UserProfile; navigate: (s: Screen) => void; mini?: boolean; backTo?: Screen; }

interface MoodEntry {
  [uid: string]: { mood: string; label: string; name: string; timestamp: any };
}

interface MoodData {
  [dateKey: string]: MoodEntry;
}

const MOODS = [
  { emoji: '😊', label: 'Mutlu' },
  { emoji: '😌', label: 'Huzurlu' },
  { emoji: '🫠', label: 'Yorgun' },
  { emoji: '😔', label: 'Düşük' },
  { emoji: '😵‍💫', label: 'Karışık' },
  { emoji: '😤', label: 'Gergin' },
];

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(dateStr: string) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' });
}

export default function MoodSync({ user, navigate, mini = false, backTo = 'bond' }: Props) {
  const [allMoods, setAllMoods] = useState<{ date: string; entry: MoodEntry }[]>([]);
  const [saving, setSaving] = useState(false);
  const today = getTodayKey();
  const partner = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';

  useEffect(() => {
    return onSnapshot(moodsDoc(user.coupleId), snap => {
      if (!snap.exists()) return;
      const data = snap.data() as MoodData;
      const sorted = Object.entries(data)
        .map(([date, entry]) => ({ date, entry }))
        .sort((a, b) => b.date.localeCompare(a.date));
      setAllMoods(sorted);
    });
  }, [user.coupleId]);

  const todayEntry = allMoods.find(m => m.date === today)?.entry || {};
  const myMoodToday = todayEntry[user.uid];
  const partnerMoodToday = Object.entries(todayEntry).find(([uid]) => uid !== user.uid);

  const setMood = async (emoji: string, label: string) => {
    setSaving(true);
    await setDoc(moodsDoc(user.coupleId), {
      [today]: {
        [user.uid]: {
          mood: emoji,
          label,
          name: user.displayName,
          timestamp: new Date().toISOString(),
        }
      }
    }, { merge: true });
    recordActivity(user, {
      type: 'mood',
      title: 'Bugünkü hal seçildi',
      body: `${user.displayName} bugün "${label}" hissini bıraktı.`,
      dayKey: today,
      sourceId: user.uid,
    }).catch(() => {});
    if (user.partnerUid) {
      queueNotification(
        user.coupleId,
        user.partnerUid,
        `${user.displayName} bugünkü halini bıraktı`,
        'Seninkiyle yan yana gelince günün rengi tamamlanacak.',
        'mood-sync',
      );
    }
    setSaving(false);
  };

  if (mini) {
    return (
      <div style={miniStyles.wrap} onClick={() => navigate('mood')}>
        <div style={miniStyles.row}>
          {myMoodToday
            ? <span style={{ fontSize: 24 }}>{myMoodToday.mood}</span>
            : <span style={{ fontSize: 20, opacity: 0.4 }}>?</span>}
          <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }}>+</span>
          {partnerMoodToday
            ? <span style={{ fontSize: 24 }}>{partnerMoodToday[1].mood}</span>
            : <span style={{ fontSize: 20, opacity: 0.4 }}>?</span>}
        </div>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Bugünkü halimiz</span>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Bugünün Havası</h1>
      </div>

      <div style={styles.body}>
        <div style={styles.syncCard}>
          <div style={styles.syncHeader}>
            <p style={styles.cardLabel}>Bugün ikimiz</p>
            <span style={styles.datePill}>{formatDate(today)}</span>
          </div>
          <div style={styles.syncRow}>
            <div style={styles.syncPerson}>
              <span style={{ ...styles.moodOrb, ...(myMoodToday ? styles.moodOrbActive : {}) }}>{myMoodToday?.mood || '—'}</span>
              <span style={styles.syncName}>{user.displayName}</span>
              <span style={styles.syncLabel}>{myMoodToday?.label || 'Seçilmedi'}</span>
            </div>
            <div style={styles.syncHeart}>+</div>
            <div style={styles.syncPerson}>
              <span style={{ ...styles.moodOrb, ...(partnerMoodToday ? styles.moodOrbActive : {}) }}>{partnerMoodToday?.[1].mood || '—'}</span>
              <span style={styles.syncName}>{partner}</span>
              <span style={styles.syncLabel}>{partnerMoodToday?.[1].label || 'Seçilmedi'}</span>
            </div>
          </div>
        </div>

        <div style={styles.todayCard}>
          <p style={styles.cardLabel}>Ben bugün</p>
          <div style={styles.moodRow}>
            {MOODS.map(m => (
              <button
                key={m.emoji}
                onClick={() => setMood(m.emoji, m.label)}
                disabled={saving}
                style={{
                  ...styles.moodBtn,
                  ...(myMoodToday?.mood === m.emoji ? styles.moodBtnActive : {}),
                }}
              >
                <span style={styles.moodEmojiBubble}>
                  <span style={styles.moodEmoji}>{m.emoji}</span>
                </span>
                <span style={styles.moodLabel}>{m.label}</span>
                {myMoodToday?.mood === m.emoji && <span style={styles.selectedDot} />}
              </button>
            ))}
          </div>
        </div>

        {/* History */}
        {allMoods.filter(m => m.date !== today).length > 0 && (
          <div>
            <p style={styles.histTitle}>Geçmiş Günler</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allMoods.filter(m => m.date !== today).slice(0, 14).map(({ date, entry }) => {
                const myEntry = entry[user.uid];
                const partEntry = Object.entries(entry).find(([uid]) => uid !== user.uid);
                return (
                  <div key={date} style={styles.histRow}>
                    <span style={styles.histDate}>{formatDate(date)}</span>
                    <div style={styles.histMoods}>
                      <span title={user.displayName}>{myEntry?.mood || '—'}</span>
                      <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>+</span>
                      <span title={partner}>{partEntry?.[1].mood || '—'}</span>
                    </div>
                    {myEntry && partEntry && myEntry.mood === partEntry[1].mood && (
                      <span style={{ fontSize: 12 }}>✨</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Inter',sans-serif", fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  body: { padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 14 },
  todayCard: {
    background: 'rgba(255,255,255,0.055)',
    border: '1px solid rgba(255,255,255,0.085)',
    borderRadius: 26,
    padding: 16,
    backdropFilter: 'blur(20px) saturate(180%)',
  },
  syncHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 },
  cardLabel: { fontSize: 13, color: 'rgba(255,255,255,0.54)', margin: 0, fontWeight: 850 },
  datePill: {
    borderRadius: 999,
    padding: '7px 10px',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.54)',
    fontSize: 12,
    fontWeight: 780,
    whiteSpace: 'nowrap',
  },
  moodRow: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 9 },
  moodBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'rgba(255,255,255,0.045)',
    border: '1px solid rgba(255,255,255,0.075)',
    borderRadius: 20,
    padding: '11px 12px',
    cursor: 'pointer',
    transition: 'transform 0.18s ease, border-color 0.18s ease, background 0.18s ease',
    minWidth: 0,
    position: 'relative',
  },
  moodEmojiBubble: {
    width: 38,
    height: 38,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255,255,255,0.06)',
    flexShrink: 0,
  },
  moodEmoji: { fontSize: 24, lineHeight: 1 },
  moodLabel: { fontSize: 13, color: 'rgba(255,255,255,0.78)', fontWeight: 830 },
  selectedDot: {
    position: 'absolute',
    right: 11,
    top: 11,
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#BF5AF2',
    boxShadow: '0 0 16px rgba(191,90,242,0.8)',
  },
  moodBtnActive: {
    background: 'rgba(191,90,242,0.16)',
    border: '1px solid rgba(191,90,242,0.48)',
    transform: 'translateY(-1px)',
  },
  syncCard: {
    background: 'linear-gradient(145deg, rgba(191,90,242,0.18), rgba(255,255,255,0.045))',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 30,
    padding: 18,
    boxShadow: '0 24px 80px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)',
    backdropFilter: 'blur(22px) saturate(180%)',
  },
  syncRow: { display: 'grid', gridTemplateColumns: '1fr 28px 1fr', alignItems: 'center', gap: 10, marginTop: 2 },
  syncPerson: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 },
  moodOrb: {
    width: 78,
    height: 78,
    borderRadius: 26,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(0,0,0,0.22)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.36)',
    fontSize: 40,
    fontWeight: 900,
  },
  moodOrbActive: {
    background: 'rgba(255,255,255,0.08)',
    color: 'white',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
  },
  syncHeart: { fontSize: 20, color: 'rgba(255,255,255,0.32)', fontWeight: 800 },
  syncName: { fontSize: 12, fontWeight: 780, color: 'rgba(255,255,255,0.74)' },
  syncLabel: { fontSize: 12, color: 'rgba(255,255,255,0.48)' },
  histTitle: { fontSize: 12, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, paddingLeft: 4 },
  histRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '10px 14px',
  },
  histDate: { fontSize: 13, color: 'rgba(255,255,255,0.5)', flex: 1 },
  histMoods: { display: 'flex', gap: 8, alignItems: 'center', fontSize: 20 },
};

const miniStyles: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer' },
  row: { display: 'flex', gap: 4, alignItems: 'center' },
};
